import {
  Chart as ChartJS,
  ArcElement,
  Legend,
  Tooltip,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
} from "chart.js";
import useDashboardApi from "../hooks/useDashboardApi";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Button, Card, Col, Row, Spin, Empty } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { FaSearch, FaChartLine } from "react-icons/fa";
import dayjs from "dayjs";
import { DATE_FORMAT, DATE_FORMAT_API } from "config/settings";
import { Line } from "react-chartjs-2";
import { DatePicker } from "@neonumy/ant-design";
import { locale } from "utils/locale";
import useCoreStore from "views/hooks/useCoreStore";
import { formatNumberWithSpaces } from "utils/formatNumberWithSpaces";
import CardTitle from "components/CardTitle";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
);

interface LineChartPropsType {
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  setEndDate: Dispatch<SetStateAction<dayjs.Dayjs>>;
  setStartDate: Dispatch<SetStateAction<dayjs.Dayjs>>;
}

export default function LineChart(props: LineChartPropsType) {
  const { startDate, endDate, setStartDate, setEndDate } = props;
  const { t } = useTranslation();
  const urlParams: RouteParams = useParams();
  const currency = useCoreStore(state => state.currency);
  const currencyCode = currency?.code;
  const decimalPlaces = currency?.decimalPlaces;
  const [chartHeight, setChartHeight] = useState(300);

  const {
    listDiaryStats,
    loadingListDiaryStats: loading,
    listDiaryStatsData: data,
    refetchListDiaryStats: refetch,
  } = useDashboardApi();

  useEffect(() => {
    if (urlParams?.companyId) {
      listDiaryStats({
        variables: {
          filter: {
            companyId: urlParams?.companyId,
            endDate: endDate && dayjs(endDate).format(DATE_FORMAT_API),
            startDate: startDate && dayjs(startDate).format(DATE_FORMAT_API),
          },
        },
      });
    }

    const updateChartHeight = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setChartHeight(250);
      } else {
        setChartHeight(300);
      }
    };

    updateChartHeight();
    window.addEventListener("resize", updateChartHeight);

    return () => window.removeEventListener("resize", updateChartHeight);
  }, [urlParams?.companyId, startDate, endDate]);

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          usePointStyle: true,
          boxWidth: 6,
          padding: 20,
          pointStyleWidth: 15,
          useBorderRadius: true,
          font: {
            size: 12,
            weight: "500" as const,
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleFont: {
          size: 13,
          weight: "bold" as const,
        },
        bodyFont: {
          size: 12,
        },
        padding: 12,
        cornerRadius: 6,
        callbacks: {
          label: function (context: { raw: number | undefined }) {
            const value = formatNumberWithSpaces(context?.raw?.toFixed(decimalPlaces));
            const label = String(value) + ` ${currencyCode}` || "";
            return label;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(0, 0, 0, 0.05)",
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11,
          },
          callback: function (value: number) {
            return formatNumberWithSpaces(value) + ` ${currencyCode}`;
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
    },
    maintainAspectRatio: false,
    elements: {
      line: {
        tension: 0.3,
        borderWidth: 2,
      },
      point: {
        radius: 3,
        hoverRadius: 5,
        hitRadius: 30,
        borderWidth: 2,
      },
    },
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
  };

  const handleOnClick = () => {
    refetch({
      filter: {
        companyId: urlParams?.companyId,
        startDate:
          dayjs(startDate)?.format(DATE_FORMAT_API) ||
          dayjs().subtract(2, "month").startOf("month").format(DATE_FORMAT_API),
        endDate: dayjs(endDate)?.format(DATE_FORMAT_API) || dayjs().format(DATE_FORMAT_API),
      },
    });
  };

  const renderChart = () => {
    if (loading) {
      return (
        <div className="chart-loading-container">
          <Spin size="large" />
        </div>
      );
    }

    if (!data || Object.keys(data).length === 0) {
      return (
        <div className="chart-empty-container">
          <Empty description={t("No data available")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      );
    }

    return (
      <Line
        width={300}
        height={chartHeight}
        data={data}
        options={options as unknown as Parameters<typeof Line>[0]["options"]}
      />
    );
  };

  return (
    <Col span={24}>
      <Card
        className="antd-newrai-card line-chart-card"
        title={<CardTitle icon={<FaChartLine />} title={t("Evolution of the business")} />}
        extra={
          <Row gutter={[12, 12]}>
            <Col md={9} sm={9} xs={24}>
              <DatePicker
                locale={locale as any}
                allowClear={false}
                format={[DATE_FORMAT, "DDMMYYYY", "DD-MM-YYYY"]}
                value={startDate}
                onChange={(date: any) => setStartDate(date)}
                placeholder={t("Start date")}
              />
            </Col>
            <Col
              style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
              xs={2}
              sm="auto"
            >
              <span className="date-separator">{t("to")}</span>
            </Col>
            <Col md={9} sm={9} xs={24}>
              <DatePicker
                locale={locale as any}
                allowClear={false}
                format={[DATE_FORMAT, "DDMMYYYY", "DD-MM-YYYY"]}
                value={endDate}
                onChange={(date: any) => setEndDate(date)}
                disabledDate={(current: dayjs.Dayjs | null) =>
                  current && current > dayjs().endOf("day")
                }
                placeholder={t("End date")}
              />
            </Col>
            <Col md={4} sm={4} xs={24} style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                type="primary"
                onClick={handleOnClick}
                icon={
                  <span className="anticon">
                    <FaSearch />
                  </span>
                }
                className="chart-view-button"
              ></Button>
            </Col>
          </Row>
        }
      >
        <div className="line-chart-container">{renderChart()}</div>
      </Card>
    </Col>
  );
}
